// File: frontend/src/utils/routerStatus.js
//
// THE single source of truth for "is this router online?" across the admin app.
//
// ── FOUR STATES, AND THE FOURTH IS THE POINT ─────────────────────────────────
//
//   online    the server confirms the router is reporting normally
//   checking  the server says it is late, but inside the grace window
//   offline   the server confirms the heartbeat is beyond the offline threshold
//   unknown   WE cannot truthfully say — our data is too old, or the last
//             refresh failed. This is a statement about the BROWSER, not the
//             router.
//
// UNKNOWN MUST NEVER BE RENDERED AS OFFLINE.
//
// That rule exists because breaking it was the production defect. The sidebar
// used to compute `Date.now() - last_seen` against whatever payload it last
// managed to fetch. The numerator advanced continuously; the denominator only
// when a fetch succeeded. So a browser that simply stopped receiving answers
// watched a healthy router "go offline" — measured 24 Aug 2026 while the router
// was heartbeating on a 30.01 s metronome and had never missed a beat. See
// docs/production-findings/ui/ROUTER_PRESENCE_LONG_LIVED_SPA_INVESTIGATION_2026-08-24.md
//
// ── WHERE PRESENCE COMES FROM ────────────────────────────────────────────────
//
// The SERVER decides online/checking/offline and sends `presence_status`. It
// has the clock, the row, and no reason to be wrong. The client's only job is
// to decide whether its copy of that answer is still fresh enough to show.
//
// The persisted `routers.is_online` / `routers.status` columns are NOT used.
// They are denormalised, event-written, and lag reality — a router can carry
// is_online=false while actively heartbeating. They remain in the payload for
// older deployed clients; nothing here reads them.

// How old a PAYLOAD may be before we stop believing our copy of it.
//
// Derived from the poll cadence, not guessed: the sidebar refreshes every 30 s,
// so a payload older than this has missed several refreshes and something is
// wrong with our connection, not with the router. Deliberately longer than one
// missed poll so an ordinary slow response does not blink the UI to unknown.
export const PAYLOAD_TRUST_SECONDS = 120;

// How old a payload may be before an EVENT is allowed to spend a request on it.
//
// Deliberately shorter than PAYLOAD_TRUST_SECONDS, and for a different job.
// PAYLOAD_TRUST_SECONDS answers "may I still show this?"; this answers "is it
// worth refetching now that the operator has done something?". Set just above
// the 30 s poll cadence so an ordinary navigation lands on data the poller has
// already refreshed and costs nothing — a request storm from clicking around
// the app is a worse outcome than a 40-second-old dot.
export const EVENT_REFRESH_SECONDS = 45;

// Fallback ladder, used ONLY when the server did not send presence_status —
// an older backend during a rollout, or the heartbeat overlay failing. Mirrors
// backend/utils/routerPresence.js. The payload also carries
// `presence_thresholds`, which is preferred over these when present.
export const ROUTER_ONLINE_SECONDS = 60;
export const ROUTER_CHECKING_SECONDS = 90;

export function getLastSeenAgeSeconds(lastSeen) {
  if (!lastSeen) return null;
  const seenAt = new Date(lastSeen).getTime();
  if (!Number.isFinite(seenAt)) return null;
  return Math.max(0, Math.floor((Date.now() - seenAt) / 1000));
}

/**
 * How long ago THIS PAYLOAD was successfully received, in seconds.
 *
 * Requires the caller to have stamped `_fetched_at` (ms) when it stored the
 * row — see stampFetched() below. Returns null when unstamped, which callers
 * must treat as "cannot judge freshness" rather than as "fresh".
 *
 * NOTE this is a DURATION between two readings of the same clock, so it is
 * immune to that clock being wrong in absolute terms. `Date.now() - last_seen`
 * is not: it subtracts a server timestamp from a browser clock, and a device
 * whose clock is three minutes fast reports every router as long dead.
 */
export function payloadAgeSeconds(r, now = Date.now()) {
  if (!r) return null;
  const fetchedAt = Number(r._fetched_at);
  if (!Number.isFinite(fetchedAt)) return null;
  return Math.max(0, (now - fetchedAt) / 1000);
}

/**
 * Age of the heartbeat in seconds, as accurately as the payload allows.
 *
 * Prefers `last_seen_age`, computed on the SERVER at response time — that
 * removes any dependence on the client's clock being correct, and on
 * `last_seen` having been serialised with an unambiguous zone.
 *
 * A server-computed age is a snapshot, so it ages with the payload: when the
 * caller stamped `_fetched_at`, the time elapsed since the fetch is added back.
 *
 * Falls back to `last_seen` arithmetic only for a backend that sends no age.
 */
export function effectiveLastSeenAge(r, now = Date.now()) {
  if (!r) return null;
  const serverAge = Number(r.last_seen_age);
  if (Number.isFinite(serverAge)) {
    const elapsed = payloadAgeSeconds(r, now) ?? 0;
    return Math.round(serverAge + elapsed);
  }
  return getLastSeenAgeSeconds(r.last_seen);
}

/**
 * Canonical presence for one router row.
 *
 * @returns {"online"|"checking"|"offline"|"unknown"}
 *
 * Resolution order:
 *   1. Our copy is too old to trust  → unknown. Checked FIRST, because a stale
 *      payload's presence_status is stale too — believing it is exactly how a
 *      dead tab keeps reporting a confident answer.
 *   2. The server told us            → use presence_status verbatim.
 *   3. No presence_status            → derive from the server-computed age
 *      (old backend / overlay failure), using the payload's own thresholds when
 *      it published them.
 *   4. No age either                 → unknown. NOT offline: we were never told
 *      anything, and inventing an outage from silence is the original bug.
 */
export function getRouterStatus(r, now = Date.now()) {
  if (!r) return "unknown";

  const payloadAge = payloadAgeSeconds(r, now);
  if (payloadAge != null && payloadAge > PAYLOAD_TRUST_SECONDS) return "unknown";

  const served = r.presence_status;
  if (served === "online" || served === "checking" || served === "offline") return served;

  const age = effectiveLastSeenAge(r, now);
  if (age == null) return "unknown";

  const onlineS = Number(r?.presence_thresholds?.online_seconds) || ROUTER_ONLINE_SECONDS;
  const checkingS = Number(r?.presence_thresholds?.checking_seconds) || ROUTER_CHECKING_SECONDS;
  if (age <= onlineS) return "online";
  if (age <= checkingS) return "checking";
  return "offline";
}

/**
 * Stamp a freshly-received router row with the moment WE received it.
 *
 * Every consumer that stores a router list must call this. Without the stamp
 * there is no way to distinguish "this router is stale" from "my data is
 * stale", and the two render identically — which is the defect.
 *
 * Also carries the payload-level `presence_thresholds` onto each row so a row
 * remains self-describing once it is separated from its response envelope.
 */
export function stampFetched(routers, envelope = null, at = Date.now()) {
  if (!Array.isArray(routers)) return [];
  const thresholds = envelope?.presence_thresholds || null;
  return routers.map((r) => (
    thresholds ? { ...r, _fetched_at: at, presence_thresholds: thresholds } : { ...r, _fetched_at: at }
  ));
}

/**
 * How old the freshest row in a stored router list is, in seconds.
 *
 * The list is fetched and stamped as a unit, so any row answers for all of
 * them; taking the freshest is simply robust against a caller that merged two
 * responses. Returns null when nothing is stamped, which callers must read as
 * "cannot judge" — and the safe move on "cannot judge" is to refresh, because
 * an unstamped list is precisely the one whose age we cannot bound.
 */
export function listPayloadAgeSeconds(routers, now = Date.now()) {
  if (!Array.isArray(routers) || routers.length === 0) return null;
  let freshest = null;
  for (const r of routers) {
    const age = payloadAgeSeconds(r, now);
    if (age == null) continue;
    if (freshest == null || age < freshest) freshest = age;
  }
  return freshest;
}

/**
 * Should an EVENT — a navigation, a router switch, a bfcache restore — spend a
 * request?
 *
 * The rule that keeps this cheap: only when our copy is actually old. Ordinary
 * clicking around the app lands inside the poll cadence and costs nothing,
 * which is what stops freshness-awareness turning into a request storm.
 */
export function shouldRefreshOnEvent(routers, now = Date.now(), maxAgeSeconds = EVENT_REFRESH_SECONDS) {
  if (!Array.isArray(routers) || routers.length === 0) return false;
  const age = listPayloadAgeSeconds(routers, now);
  if (age == null) return true;          // unstamped: we cannot bound it, so refresh
  return age > maxAgeSeconds;
}

// True only for a CONFIRMED healthy router. `unknown` is not online — but it is
// not offline either, so never write `!isOnline(r)` and label the result
// "Offline". Ask for the status and handle all four states.
export const isOnline = (r) => getRouterStatus(r) === "online";

// Safe for the one question "may I draw this as part of the working network?".
// A router one missed beat late still is; a router we cannot see is not claimed.
export const isReachable = (r) => {
  const s = getRouterStatus(r);
  return s === "online" || s === "checking";
};

// Human label for each state. `unknown` deliberately talks about US.
export const STATUS_LABEL = {
  online: "Online",
  checking: "Checking",
  offline: "Offline",
  unknown: "Not updating",
};
